import React, { useEffect, useState, useMemo } from 'react';
import { getFileType, getThumbnailUrl } from './utils/photoHelpers';

/**
 * PhotoGrid component handles the display of photos in a virtualized responsive grid
 * @param {Object} props
 * @param {Array} props.photos - Array of photo filenames
 * @param {boolean} props.selectionMode - Whether selection mode is active
 * @param {Set} props.selectedPhotos - Set of selected photo filenames
 * @param {boolean} props.isDraggingSelection - Whether drag selection is active
 * @param {string} props.lastSelectedPhoto - Last selected photo filename
 * @param {function} props.onPhotoClick - Handler for photo click (selection or view)
 * @param {function} props.bindSelectionGesture - Gesture binding for drag selection
 * @param {boolean} props.hasNextPage - Whether there are more photos to load
 * @param {boolean} props.isFetchingNextPage - Whether next page is loading
 * @returns {JSX.Element}
 */
const PhotoGrid = ({
  photos = [],
  selectionMode = false,
  selectedPhotos = new Set(),
  isDraggingSelection = false,
  lastSelectedPhoto = null,
  onPhotoClick = () => { },
  bindSelectionGesture = () => ({}),
  hasNextPage = false,
  isFetchingNextPage = false,
  filterType = 'all'
}) => {

  // Local grid configuration state
  const [localGridConfig, setLocalGridConfig] = useState(() => {
    const width = window.innerWidth;
    let columnCount = 3;
    let itemSize = 120;

    if (width >= 1024) {
      columnCount = 5;
      itemSize = Math.floor((width - 32) / columnCount);
    } else if (width >= 768) {
      columnCount = 4;
      itemSize = Math.floor((width - 24) / columnCount);
    } else if (width >= 640) {
      columnCount = 4;
      itemSize = Math.floor((width - 16) / columnCount);
    } else {
      columnCount = 3;
      itemSize = Math.floor((width - 16) / columnCount);
    }

    itemSize = Math.max(itemSize, 100);

    return {
      columnCount,
      itemSize,
    };
  });

  // Safe grid config (memoized to prevent unnecessary re-renders)
  const safeGridConfig = useMemo(() =>
    localGridConfig || { columnCount: 3, itemSize: 120 },
    [localGridConfig]
  );

  // Memory management is handled at the PhotoGallery level

  // Track images that have fallen back to logos and periodically retry loading thumbnails
  const [fallbackImages, setFallbackImages] = useState(new Set());

  // Periodically retry loading thumbnails for images that fell back to logos
  useEffect(() => {
    if (fallbackImages.size === 0) return;

    const retryInterval = setInterval(() => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔄 Retrying ${fallbackImages.size} thumbnail(s)...`);
      }
      fallbackImages.forEach((filename) => {
        // Find the container div first, then the img element inside it
        const container = document.querySelector(`[data-photo-filename="${filename}"]`);
        const img = container?.querySelector('img');
                      if (img && (img.src.includes('/UserData/Logo.svg') || img.src.includes('/logo512.png'))) {
          // Try to reload the thumbnail with cache-busting to bypass browser cache
          const thumbUrl = `${getThumbnailUrl(filename)}?t=${Date.now()}`;
          const testImg = new Image();
          testImg.onload = () => {
            // Thumbnail is now available, update the image source (without cache-busting for normal display)
            const cleanThumbUrl = getThumbnailUrl(filename);
            img.src = cleanThumbUrl;
            img.className = 'w-full h-full object-cover';
            setFallbackImages(prev => {
              const newSet = new Set(prev);
              newSet.delete(filename);
              return newSet;
            });
            console.log(`✅ Thumbnail now available for ${filename}: ${cleanThumbUrl}`);
          };
          testImg.onerror = () => {
            // Thumbnail still not available, keep it in fallback set for next retry
            console.debug(`Thumbnail still not available for retry: ${thumbUrl}`);
          };
          testImg.src = thumbUrl;
        }
      });
    }, 5000); // Retry every 5 seconds

    return () => clearInterval(retryInterval);
  }, [fallbackImages]);



  // Update grid config when window resizes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      let columnCount = 3;
      let itemSize = 120;

      if (width >= 1024) {
        columnCount = 5;
        itemSize = Math.floor((width - 32) / columnCount);
      } else if (width >= 768) {
        columnCount = 4;
        itemSize = Math.floor((width - 24) / columnCount);
      } else if (width >= 640) {
        columnCount = 4;
        itemSize = Math.floor((width - 16) / columnCount);
      } else {
        columnCount = 3;
        itemSize = Math.floor((width - 16) / columnCount);
      }

      itemSize = Math.max(itemSize, 100);

      setLocalGridConfig({
        columnCount,
        itemSize,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Calculate row count
  const rowCount = photos && safeGridConfig ? Math.ceil(photos.length / safeGridConfig.columnCount) : 0;


  // Don't render Grid if we don't have valid data
  // Return null and let parent component handle loading states
  if (!photos || photos.length === 0 || !safeGridConfig || rowCount === 0) {
    return null;
  }

  try {
    return (
      <div
        className={`photo-gallery-container px-4 pt-2 pb-4 sm:px-6 sm:pb-24 ${isDraggingSelection ? 'selecting' : ''
          }`}
        {...(selectionMode ? bindSelectionGesture() : {})}
        role="grid"
        aria-label={selectionMode ? "Photo gallery in selection mode" : "Photo gallery"}
        style={{ width: '100%' }}
      >
        <div
          className="photo-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${safeGridConfig.columnCount}, 1fr)`,
            gap: '0.5rem',
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          {photos.map((filename, index) => {
            if (!filename || typeof filename !== 'string') return null;

            const fileType = getFileType(filename);
            const thumbUrl = getThumbnailUrl(filename);
            const isSelected = selectedPhotos.has(filename);
            const photoNumber = index + 1;

            // Debug URL construction for first few images
            if (process.env.NODE_ENV === 'development' && index < 3) {
              console.log(`📸 PhotoGrid URL for ${filename}:`, {
                fileType,
                thumbUrl: thumbUrl.substring(0, 100) + '...',
                original: filename
              });
            }


            return (
              <div
                key={`${filename}-${index}`}
                style={{
                  width: '100%',
                  height: safeGridConfig.itemSize
                }}
                className={`group relative overflow-hidden rounded shadow-md transition-transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-50 focus:ring-offset-2 ${selectionMode ? 'cursor-pointer' : 'cursor-pointer'
                  } ${isSelected ? 'selected' : ''} ${isDraggingSelection && lastSelectedPhoto === filename ? 'drag-selecting' : ''
                  }`}
                onClick={() => onPhotoClick(filename)}
                role="gridcell"
                aria-selected={selectionMode ? isSelected : undefined}
                aria-label={selectionMode
                  ? `${fileType === 'video' ? 'Video' : 'Photo'} ${photoNumber} of ${photos.length}${isSelected ? ' - selected' : ''}`
                  : `${fileType === 'video' ? 'Video' : 'Photo'} ${photoNumber} of ${photos.length}, click to view`
                }
                tabIndex={selectionMode ? 0 : -1}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onPhotoClick(filename);
                  }
                }}
                data-photo-filename={filename}
              >
                {fileType === 'video' ? (
                  <div className="video-thumbnail relative w-full h-full">
                    <img
                      ref={(el) => {
                        // Memory management handled at PhotoGallery level
                      }}
                      src={thumbUrl}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      alt="Video"
                      onLoad={() => {
                        // Remove from fallback set if it loads successfully
                        if (fallbackImages.has(filename)) {
                          setFallbackImages(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(filename);
                            return newSet;
                          });
                        }
                      }}
                      onError={(e) => {
                        // Only log 404 errors once per image to avoid spam
                        if (!fallbackImages.has(filename)) {
                          console.warn('Video thumbnail failed to load (404):', thumbUrl);
                        }
                        // Fallback to app logo if thumbnail fails to load (with cache-busting to prevent caching)
                        e.target.src = `/UserData/Logo.svg?t=${Date.now()}`;
                        e.target.className = 'w-full h-full object-contain bg-primary-10';
                        setFallbackImages(prev => new Set([...prev, filename]));
                      }}
                    />
                    {selectionMode && (
                      <div className={`selection-indicator ${selectedPhotos.has(filename)
                        ? 'bg-primary border-accent'
                        : 'bg-white border-gray-300'
                        }`}>
                        {selectedPhotos.has(filename) && (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    )}

                    <div className="video-indicator">
                      <svg className="w-2 h-2 sm:w-3 sm:h-3 inline mr-0.5 sm:mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                      </svg>
                      <span className="hidden sm:inline">VIDEO</span>
                      <span className="sm:hidden">Video</span>
                    </div>
                  </div>
                ) : (
                  <div className="relative w-full h-full">
                    <img
                      ref={(el) => {
                        // Memory management handled at PhotoGallery level
                      }}
                      src={thumbUrl}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      alt="Wedding"
                      onLoad={() => {
                        // Remove from fallback set if it loads successfully
                        if (fallbackImages.has(filename)) {
                          setFallbackImages(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(filename);
                            return newSet;
                          });
                        }
                      }}
                      onError={(e) => {
                        // Only log 404 errors once per image to avoid spam
                        if (!fallbackImages.has(filename)) {
                          console.warn('Photo thumbnail failed to load (404):', thumbUrl);
                        }
                        // Fallback to app logo if thumbnail fails to load (with cache-busting to prevent caching)
                        e.target.src = `/logo512.png?t=${Date.now()}`;
                        e.target.className = 'w-full h-full object-contain bg-primary-10';
                        setFallbackImages(prev => new Set([...prev, filename]));
                      }}
                    />
                    {selectionMode && (
                      <div className={`selection-indicator ${selectedPhotos.has(filename)
                        ? 'bg-primary border-white'
                        : 'bg-white border-gray-300'
                        }`}>
                        {selectedPhotos.has(filename) && (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

      </div>
    );
  } catch (error) {
    console.error('Error in PhotoGrid render:', error);
    return (
      <div className="photo-gallery-container px-4 pt-4 pb-20 sm:px-6 sm:pb-24">
        <div className="flex items-center justify-center py-8">
          <div className="text-red-500">Error loading photo gallery</div>
        </div>
      </div>
    );
  }
};

export default PhotoGrid;
